import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Collapse, IconButton, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { formatDate } from 'share/utils';
import {
  IvrCallStatusSummary,
  IVR_LIVE_PHASES,
} from 'modules/patients/interfaces';
import { useIvrCalls } from './ivrCalls';
import { ivrPhaseColor, ivrPhaseLabel } from './IvrPhaseChip';
import IvrRecording from './IvrRecording';

const PhaseBadge: React.FC<{ call: IvrCallStatusSummary }> = ({ call }) => (
  <Box
    component="span"
    px={0.75}
    style={{
      backgroundColor: ivrPhaseColor(call.phase),
      color: '#fff',
      borderRadius: 3,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      lineHeight: '18px',
      display: 'inline-block',
    }}
  >
    {ivrPhaseLabel(call.phase)}
  </Box>
);

const CallRow: React.FC<{ call: IvrCallStatusSummary }> = ({ call }) => {
  const facts = useMemo(() => {
    const parts: string[] = [];
    if (typeof call.answeredCount === 'number' && call.answeredCount > 0) {
      parts.push(`${call.answeredCount} answered`);
    }
    // Counts call status transitions, not dials -- one call produces several.
    if (typeof call.attemptCount === 'number' && call.attemptCount > 0) {
      parts.push(`${call.attemptCount} call events`);
    }
    if (typeof call.retriesRemaining === 'number') {
      parts.push(`${call.retriesRemaining} attempt(s) remaining`);
    }
    if (call.nextRetryAt) {
      parts.push(`next attempt ${formatDate(call.nextRetryAt, true, true)}`);
    }
    if (call.lastActivityAt) {
      parts.push(`updated ${formatDate(call.lastActivityAt, true, true)}`);
    }
    return parts.join(' · ');
  }, [call]);

  return (
    <Box
      py={1}
      style={{ borderTop: '1px solid #eee' }}
      display="flex"
      flexWrap="wrap"
      alignItems="flex-start"
    >
      <Box mr={1} mt={0.25}>
        <PhaseBadge call={call} />
      </Box>
      <Box flexGrow={1} minWidth={220}>
        <Typography variant="body2">{call.description}</Typography>
        {facts && (
          <Typography variant="caption" color="textSecondary" component="div">
            {facts}
          </Typography>
        )}
      </Box>
      <Box mt={0.5}>
        <IvrRecording recordingUrl={call.recordingUrl} callSid={call.callSid} />
      </Box>
    </Box>
  );
};

/**
 * Every phone-survey call placed to this patient, most recent first.
 *
 * Answers "how many times have we rung this person?" directly. Renders nothing
 * when the patient has never been called, so it stays out of the way for the
 * majority of patients who are contacted by SMS or email.
 */
export const IvrCallHistory: React.FC = () => {
  const { calls, loading } = useIvrCalls();

  const [open, setOpen] = useState(false);
  const autoExpanded = useRef(false);

  const hasLiveCall = useMemo(
    () =>
      calls.some(call => !!call?.phase && IVR_LIVE_PHASES.includes(call.phase)),
    [calls],
  );

  // A call in flight is the moment someone cares, so open it once on its own.
  // Only once, so it can still be collapsed and stay collapsed.
  useEffect(() => {
    if (hasLiveCall && !autoExpanded.current) {
      autoExpanded.current = true;
      setOpen(true);
    }
  }, [hasLiveCall]);

  if (loading || calls.length === 0) {
    return null;
  }

  return (
    <Box mb={1} style={{ border: '1px solid #eee', borderRadius: 6 }} px={1.5}>
      <Box display="flex" alignItems="center">
        <IconButton size="small" onClick={() => setOpen(!open)}>
          {open ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <Typography variant="subtitle2" className="ml-8">
          {`Phone call history (${calls.length})`}
        </Typography>
      </Box>
      <Collapse in={open}>
        <Box pb={1}>
          {calls.map((call, index) => (
            <CallRow key={call.callSid || index} call={call} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default React.memo(IvrCallHistory);
